<template>
  <StackPageLayout @close="navigateToMoreMenu">
    <template #title> Service Charge Settings </template>
    <div class="space-y-6">
      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Service Charge
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            :model-value="tenant.isServiceChargeEnabled"
            :icon="IconReceipt"
            label="Service Charge"
            description="Enable service charge"
            @update:model-value="
              (value) =>
                updateSetting(
                  'isServiceChargeEnabled',
                  value
                )
            "
          />
          <div
            v-if="tenant.isServiceChargeEnabled"
            class="flex flex-col space-y-2 p-3"
          >
            <div>
              <label
                class="mb-1 block text-xs font-medium text-background-fg/70"
                for="defaultServiceChargePercentage"
              >
                Default Percentage Service Charge
              </label>
              <BasePercentageInput
                name="defaultServiceChargePercentage"
                :disabled="!tenant.isServiceChargeEnabled"
                :model-value="
                  tenant.defaultServiceChargePercentage ?? 0
                "
                :highlight="
                  tenant.isServiceChargeEnabled &&
                  !!tenant.defaultServiceChargePercentage
                "
                :min="0"
                @update:model-value="
                  (value) =>
                    updateSetting(
                      'defaultServiceChargePercentage',
                      value
                    )
                "
              />
            </div>
            <div>
              <label
                class="mb-1 block text-xs font-medium text-background-fg/70"
                for="defaultServiceChargeFlat"
              >
                Default Flat Service Charge
              </label>
              <BasePriceInput
                v-model="defaultServiceChargeFlatModel"
                name="defaultServiceChargeFlat"
                :disabled="!tenant.isServiceChargeEnabled"
                :highlight="
                  tenant.isServiceChargeEnabled &&
                  !!tenant.defaultServiceChargeFlat
                "
                :min="0"
              />
            </div>
          </div>
        </MoreMenuSubsection>
      </MoreMenuSection>
      <MoreMenuSection v-if="tenant.isServiceChargeEnabled">
        <MoreMenuSectionTitle>
          Example
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <div class="p-3">
            <div
              class="flex items-center justify-between space-x-5"
            >
              <div class="text-xs text-background-fg/70">
                Subtotal
              </div>
              <div class="text-sm font-semibold">
                {{
                  formatToMerchantCurrencyString(
                    serviceChargeExamplePrice
                  )
                }}
              </div>
            </div>
            <div
              class="flex items-center justify-between space-x-5"
            >
              <div
                class="text-xs font-semibold text-background-fg/70"
              >
                Service charge ({{
                  tenant.defaultServiceChargePercentage
                }}%)
              </div>

              <div class="text-sm font-semibold">
                {{
                  formatToMerchantCurrencyString(
                    serviceChargePercentageAmount
                  )
                }}
              </div>
            </div>
            <div
              class="flex items-center justify-between space-x-5"
            >
              <div
                class="text-xs font-semibold text-background-fg/70"
              >
                Service charge (Flat)
              </div>
              <div class="text-sm font-semibold">
                {{
                  formatToMerchantCurrencyString(
                    serviceChargeFlatAmount
                  )
                }}
              </div>
            </div>
            <div
              class="flex items-center justify-between space-x-5 border-t"
            >
              <div class="text-xs text-background-fg/70">
                Total
              </div>
              <div class="text-sm font-semibold">
                {{ formatToMerchantCurrencyString(total) }}
              </div>
            </div>
          </div>
        </MoreMenuSubsection>
      </MoreMenuSection>
    </div>
  </StackPageLayout>
</template>

<script setup lang="ts">
import { IconReceipt } from "@/icons"
import { partialUpdateSettings } from "@/services/settings/partialUpdateSettings"
import type { TenantState } from "@/composables/useTenant"

const { formatToMerchantCurrencyString } = useCurrency()
const {
  tenant,

  setTenantState,
} = useTenant()

const router = useRouter()

const updateSetting = async (
  key: keyof TenantState,
  value: TenantState[typeof key]
) => {
  try {
    const newState = await partialUpdateSettings({
      [key]: value,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to update setting")
  }
}

const navigateToMoreMenu = () => {
  router.push({ name: "moreMenu" })
}

const serviceChargeExamplePrice = 10000
const serviceChargePercentageAmount = computed(() =>
  Math.round(
    ((tenant.defaultServiceChargePercentage ?? 0) / 100) *
      serviceChargeExamplePrice
  )
)
const serviceChargeFlatAmount = computed(
  () => tenant.defaultServiceChargeFlat ?? 0
)
const serviceChargeTotal = computed(() =>
  Math.max(
    serviceChargePercentageAmount.value +
      serviceChargeFlatAmount.value,
    0
  )
)
const total = computed(
  () => serviceChargeExamplePrice + serviceChargeTotal.value
)

const defaultServiceChargeFlatModel = computed({
  get: () => tenant.defaultServiceChargeFlat ?? 0,
  set: (value: number) =>
    updateSetting("defaultServiceChargeFlat", value),
})
</script>
